import { Edit } from '@mui/icons-material';
import {
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
} from '@mui/material';

import { Comment } from 'api/models/Comment';
import { Field } from 'api/models/Form';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import {
  getCommentIcon,
  getCommentIconColor,
} from 'routes/Form/components/CommentButton';
import { formsFieldTemplatesMap } from 'state/formState';
import { getDateTimeString } from 'utils';
import { DateFieldDateTypes } from '../AddFieldTemplate/utils';

interface CommentHistoryTableProps {
  comments: Comment[];
  history: Field[];
  loading: boolean;
}

export default function CommentHistoryTable({
  comments,
  loading,
  history,
}: CommentHistoryTableProps) {
  const { t } = useTranslation();
  const rows = [...history, ...comments].sort((a, b) =>
    moment(a.updatedAt).isAfter(moment(b.updatedAt)) ? -1 : 1,
  ) as Comment[];

  const fieldTemplateMap = useRecoilValue(formsFieldTemplatesMap);

  const getStringValue = (value: string, fieldTemplateId: string) => {
    const dateType =
      fieldTemplateMap.get(fieldTemplateId)?.fieldAttributes.dateType;
    if (dateType === DateFieldDateTypes.Time) {
      return value.split(' ')[1];
    }
    if (dateType === DateFieldDateTypes.Date) {
      return value.split(' ')[0];
    }

    return value;
  };

  const getStatusText = (comment: Comment) => {
    if (comment.status) {
      return t(`dialogs.commentsDialog.status.${comment.status}`);
    }

    return t(`dialogs.commentsDialog.dataUpdate`);
  };

  return (
    <TableContainer sx={{ mb: 4 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }} align="left">
              {t('general.date')}
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold' }} align="left">
              {t('dialogs.commentsDialog.user')}
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold' }} align="left">
              {t('general.status')}
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold' }} align="left">
              {t('dialogs.commentsDialog.value')}
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold' }} align="left">
              {t('dialogs.commentsDialog.comment')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <LinearProgress />
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6}>
                <Typography align="center">{t('general.noData')}</Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map(c => (
              <TableRow key={`history-table-row-${c.id}`}>
                <TableCell align="left">
                  {getDateTimeString(c.updatedAt, 'YYYY.MM.DD HH:mm:ss')}
                </TableCell>
                <TableCell align="left">{c.updatedBy}</TableCell>
                <TableCell align="left">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Box
                      sx={{
                        mr: 1,
                        width: '35px',
                        height: '35px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        backgroundColor: theme =>
                          getCommentIconColor(c.status, theme),
                      }}
                    >
                      {c.status ? (
                        getCommentIcon({
                          status: c.status,
                          size: 'small',
                          color: 'white',
                        })
                      ) : (
                        <Edit fontSize="small" sx={{ color: 'white' }} />
                      )}
                    </Box>
                    {getStatusText(c)}
                  </Box>
                </TableCell>
                <TableCell align="left" sx={{ whiteSpace: 'nowrap' }}>
                  {getStringValue(c.value, c.fieldTemplateId)}
                </TableCell>
                <TableCell align="left">{c.text ?? ''}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
